/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2018. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { ReduxUtils } from '@spm/core';
import { combineReducers } from 'redux';
import { GET_SCREENING_RESULTS_ACTION_TYPES, START_SCREENING_FORM_TYPE, START_FILTERED_SCREENING_FORM_TYPE, SELECT_IN_PROGRESS_SCREENINGS_ACTION_TYPES, SELECT_COMPLETED_SCREENINGS_ACTION_TYPES, UPDATE_SCREENING_FORM_PROGRAMS_ACTION_TYPES } from './actionTypes';

/**
 * Updates the Screening Form details of the 'UAScreening' store object.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function createScreeningFormReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateReducer(state, action, START_SCREENING_FORM_TYPE);
}

/**
 * Updates the indicator of creation of Screening Form details of the 'UAScreening' store object.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function isCreatingScreeningFormReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateFetchingReducer(state, action, START_SCREENING_FORM_TYPE);
}

/**
 * Updates the post data errors for the create screening form.
 *
 * @param {*} state
 * @param {*} action
 */
function createScreeningFormErrorReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateErrorReducer(state, action, START_SCREENING_FORM_TYPE);
}

/**
 * Updates the screening results data within the Redux store.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function screeningResultsReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateReducer(state, action, GET_SCREENING_RESULTS_ACTION_TYPES);
}

/**
 * Updates the flag that is set while the get data request is in progress for the screening results
 * page.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function isFetchingScreeningResultsReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateFetchingReducer(state, action, GET_SCREENING_RESULTS_ACTION_TYPES);
}

/**
 * Updates the get data errors for the screening results page.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function screeningResultsErrorReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateErrorReducer(state, action, GET_SCREENING_RESULTS_ACTION_TYPES);
}

/**
 * Updates the post data errors for the create filtered screening form.
 *
 * @param {*} state
 * @param {*} action
 */
function createFilteredScreeningFormErrorReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateErrorReducer(state, action, START_FILTERED_SCREENING_FORM_TYPE);
}

/**
 * Updates the in-progress screenings data within the Redux store.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function selectInProgressScreeningsReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateReducer(state, action, SELECT_IN_PROGRESS_SCREENINGS_ACTION_TYPES);
}

/**
 * Updates the flag that is set while the get data request is in progress for the in-progress
 * screenings.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function isFetchingInProgressScreeningsReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateFetchingReducer(state, action, SELECT_IN_PROGRESS_SCREENINGS_ACTION_TYPES);
}

/**
 * Updates the get data errors for the in-progress screenings.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function selectInProgressScreeningsErrorReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateErrorReducer(state, action, SELECT_IN_PROGRESS_SCREENINGS_ACTION_TYPES);
}

/**
 * Updates the completed screenings data within the Redux store.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function selectCompletedScreeningsReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateReducer(state, action, SELECT_COMPLETED_SCREENINGS_ACTION_TYPES);
}

/**
 * Updates the flag that is set while the get data request is in progress for the completed
 * screenings.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function isFetchingCompletedScreeningsReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateFetchingReducer(state, action, SELECT_COMPLETED_SCREENINGS_ACTION_TYPES);
}

/**
 * Updates the get data errors for the completed screenings.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function selectCompletedScreeningsErrorReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateErrorReducer(state, action, SELECT_COMPLETED_SCREENINGS_ACTION_TYPES);
}

/**
 * Updates the indicator that we are currently updating the Screening Form programs.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function isUpdatingScreeningFormProgramsReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateFetchingReducer(state, action, UPDATE_SCREENING_FORM_PROGRAMS_ACTION_TYPES);
}

/**
 * Updates the post data errors for updating the screening form programs.
 *
 * @param {*} state
 * @param {*} action
 */
function updateScreeningFormProgramsErrorReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateErrorReducer(state, action, UPDATE_SCREENING_FORM_PROGRAMS_ACTION_TYPES);
}
var reducers = combineReducers({
  createScreeningForm: createScreeningFormReducer,
  isCreatingApplicationForm: isCreatingScreeningFormReducer,
  createScreeningFormError: createScreeningFormErrorReducer,
  createFilteredScreeningFormError: createFilteredScreeningFormErrorReducer,
  screeningResults: screeningResultsReducer,
  isFetchingScreeningResults: isFetchingScreeningResultsReducer,
  screeningResultsError: screeningResultsErrorReducer,
  selectInProgressScreenings: selectInProgressScreeningsReducer,
  isFetchingInProgressScreenings: isFetchingInProgressScreeningsReducer,
  selectInProgressScreeningsError: selectInProgressScreeningsErrorReducer,
  selectCompletedScreenings: selectCompletedScreeningsReducer,
  isFetchingCompletedScreenings: isFetchingCompletedScreeningsReducer,
  selectCompletedScreeningsError: selectCompletedScreeningsErrorReducer,
  isUpdatingScreeningFormPrograms: isUpdatingScreeningFormProgramsReducer,
  updateScreeningFormProgramsError: updateScreeningFormProgramsErrorReducer
});
export default {
  reducers: reducers
};